import { isDebug } from "@/constants";
import { animationOverrides } from "@/constants/animationOverrides";
import { customSetManifestFileName } from "@/constants/diceConfig";
import { manifestOverrides } from "@/constants/manifestOverrides";
import { particleSystemOverrides } from "@/constants/particleSystemOverrides";
import { shaderOverrides } from "@/constants/shaderOverrides";
import type {
  AnimationGroup,
  CustomUrlMap,
  Manifest,
  ManifestResponse,
  ParticleSystem,
  Shader,
} from "@/types";
import { merge } from "lodash";

interface GetManifestDataParams {
  mediaUrl: string;
  setId: string;
  manifestUrl: string;
  customUrlMap?: CustomUrlMap;
}

export const getManifestData = async ({
  mediaUrl,
  setId,
  manifestUrl,
  customUrlMap,
}: GetManifestDataParams) => {
  if (!manifestUrl) {
    if (isDebug) {
      console.log(`[getManifestData] No manifest URL for setId: ${setId}`);
    }

    return;
  }

  let manifestResponse = (await (
    await fetch(
      customUrlMap
        ? customUrlMap[customSetManifestFileName]
        : `${mediaUrl}/dice${manifestUrl}`,
    )
  ).json()) as ManifestResponse;

  const overrides = manifestOverrides[setId];
  if (overrides) {
    manifestResponse = merge({}, manifestResponse, overrides);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { $schema, setName, resources, ...nodes } = manifestResponse;

  const setCodename = customUrlMap
    ? setId
    : (manifestUrl?.match(/\/bundles\/(.+)\/[^/]+$/)?.[1] ?? "");

  let animationGroups: Record<string, AnimationGroup> | undefined;
  if (resources.animationGroups) {
    animationGroups = {};

    for (const [key, value] of Object.entries(resources.animationGroups)) {
      const animationKey =
        // of course rime is the only set that uses object
        typeof resources.animationGroups[key] === "object"
          ? resources.animationGroups[key].animationJson
          : resources.animationGroups[key];
      if (!animationKey) {
        continue;
      }

      const animation = await (
        await fetch(
          customUrlMap
            ? customUrlMap[value as string]
            : `${mediaUrl}/dice/bundles/${setCodename}/${animationKey}`,
        )
      ).json();

      if (typeof resources.animationGroups[key] === "object") {
        for (const [cloneKey, cloneValue] of Object.entries(
          resources.animationGroups[key].animationClones,
        )) {
          animationGroups[cloneKey] = merge({}, animation, cloneValue);
        }
      } else {
        animationGroups[key] = animation;
      }

      const overrides = animationOverrides[setId]?.[key];
      if (overrides) {
        animationGroups[key] = merge({}, animationGroups[key], overrides);
      }
    }
  }

  let particleSystems: Record<string, ParticleSystem> | undefined;
  if (resources.particleSystems) {
    particleSystems = {};

    for (const key of Object.keys(resources.particleSystems)) {
      const fileKey = resources.particleSystems[key].particleJson;
      const data = await fetch(
        customUrlMap
          ? customUrlMap[fileKey as string]
          : `${mediaUrl}/dice/bundles/${setCodename}/${fileKey}`,
      );
      const json = await data.json();
      let particleSystem = json["systems"]?.[0];

      const psOverrides = particleSystemOverrides[setId]?.[key];
      if (psOverrides) {
        particleSystem = merge({}, particleSystem, psOverrides);
      }

      particleSystems[key] = particleSystem;
    }
  }

  let shaders: Record<string, Shader> | undefined;
  if (resources.shaders) {
    shaders = {};

    for (const [key, { fragmentShader: fragmentShaderKey }] of Object.entries(
      resources.shaders,
    )) {
      if (!fragmentShaderKey) {
        continue;
      }

      let fragmentShader = await (
        await fetch(
          customUrlMap
            ? customUrlMap[fragmentShaderKey]
            : `${mediaUrl}/dice/bundles/${setCodename}/${fragmentShaderKey}`,
        )
      ).text();

      const overrides = shaderOverrides[setId]?.[key];
      if (overrides?.length) {
        for (const { searchValue, replaceValue, replaceAll } of overrides) {
          if (replaceAll) {
            fragmentShader = fragmentShader.replaceAll(
              searchValue,
              replaceValue,
            );
          } else {
            fragmentShader = fragmentShader.replace(searchValue, replaceValue);
          }
        }
      }

      shaders[key] = {
        vertexShader: "",
        fragmentShader,
      };
    }
  }

  return {
    id: setCodename,
    slug: setCodename,
    resources,
    animationGroups,
    particleSystems,
    shaders,
    ...nodes,
  } as Manifest;
};
